import React, {type ReactNode, type ComponentPropsWithoutRef} from 'react';
import {EzModal, EzTextStyle, EzLayout, EzFormLayout, EzField, EzAlert} from '@ezcater/recipe';
import {type ApolloError, useMutation, gql} from '@apollo/client';
import {Form, Formik, useFormikContext, type FormikHelpers} from 'formik';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';
import formattedOrderNumber from '../../../util/formattedOrderNumber';
import type {
  DeliveryManagementCreateDriverModalDriverCreateMutation,
  DeliveryManagementCreateDriverModalDriverCreateMutationVariables,
} from '../../../__generated__/DeliveryManagementCreateDriverModalDriverCreateMutation';
import StoresSelect, {SELECT_ALL_OPTION_VALUE} from '../StoresSelect';

const validationSchema = yup.object().shape({
  ezcaterStoreIds: yup.array().min(1, 'At least one store is required.').ensure().required(),
  firstName: yup.string().required('First name is required.'),
  lastName: yup.string().required('Last name is required.'),
  makeModel: yup.string().notRequired(),
  phone: yup
    .string()
    .min(10, 'Phone number must be 10 digits.')
    .required('Phone number is required.'),
});

type FormFields = {
  ezcaterStoreIds: string[];
  firstName: string;
  lastName: string;
  makeModel?: string;
  phone: string;
};

type StoresSelectProps = ComponentPropsWithoutRef<typeof StoresSelect>;

type CreateDriverFormProps = {
  children?: ReactNode;
  isAdditional?: boolean;
  isOpen: boolean;
  onDismiss: () => void;
  orderNumber: string;
  stores: StoresSelectProps['stores'];
};

const CreateDriverForm = ({
  children,
  isAdditional,
  stores,
  orderNumber,
  isOpen,
  onDismiss,
}: CreateDriverFormProps) => {
  const [t] = useTranslation();
  const {
    errors,
    handleChange,
    handleReset,
    isSubmitting,
    setFieldValue,
    submitForm,
    touched,
    values,
  } = useFormikContext<FormFields>();

  const handleStoresSelectChange: StoresSelectProps['onChange'] = selected => {
    setFieldValue('ezcaterStoreIds', selected);
  };

  const handleModalDismiss = () => {
    handleReset();
    onDismiss();
  };

  return (
    <Form>
      <EzModal
        dismissLabel={t(
          'components.deliveryManagement.assignDriver.createDriverModal.dismissLabel'
        )}
        headerText={t('components.deliveryManagement.assignDriver.createDriverModal.headerText', {
          orderNumber: formattedOrderNumber(orderNumber),
        })}
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        onDismiss={handleModalDismiss}
        onSubmit={submitForm}
        submitLabel={t('components.deliveryManagement.assignDriver.createDriverModal.submitLabel')}
      >
        <EzLayout layout="stack">
          {!isAdditional && (
            <EzTextStyle>
              {t('components.deliveryManagement.assignDriver.createDriverModal.emptyDriversPrompt')}
            </EzTextStyle>
          )}
          <EzTextStyle>
            {t('components.deliveryManagement.assignDriver.createDriverModal.subheader')}
          </EzTextStyle>
          <EzFormLayout>
            <EzLayout layout="stack">
              <EzLayout layout="equal">
                <EzField
                  label={t(
                    'components.deliveryManagement.assignDriver.createDriverModal.fields.firstNameLabel'
                  )}
                  name="firstName"
                  value={values.firstName}
                  error={errors.firstName}
                  touched={touched.firstName}
                  onChange={handleChange}
                />
                <EzField
                  label={t(
                    'components.deliveryManagement.assignDriver.createDriverModal.fields.lastNameLabel'
                  )}
                  name="lastName"
                  value={values.lastName}
                  error={errors.lastName}
                  touched={touched.lastName}
                  onChange={handleChange}
                />
              </EzLayout>
              <EzLayout layout="equal">
                <EzField
                  label={t(
                    'components.deliveryManagement.assignDriver.createDriverModal.fields.phoneLabel'
                  )}
                  name="phone"
                  value={values.phone}
                  error={errors.phone}
                  touched={touched.phone}
                  onChange={handleChange}
                />
                <EzField
                  label={t(
                    'components.deliveryManagement.assignDriver.createDriverModal.fields.makeModelLabel'
                  )}
                  name="makeModel"
                  value={values.makeModel}
                  error={errors.makeModel}
                  touched={touched.makeModel}
                  onChange={handleChange}
                />
              </EzLayout>
              <span>
                <StoresSelect
                  stores={stores.filter(Boolean)}
                  value={values.ezcaterStoreIds}
                  error={errors.ezcaterStoreIds}
                  touched={touched.ezcaterStoreIds}
                  onChange={handleStoresSelectChange}
                  menuPosition="fixed"
                  maxMenuHeight={152}
                />
              </span>
            </EzLayout>
          </EzFormLayout>
          {children}
        </EzLayout>
      </EzModal>
    </Form>
  );
};

type CreateDriverModalProps = Pick<
  CreateDriverFormProps,
  'stores' | 'isAdditional' | 'isOpen' | 'onDismiss'
> & {
  currentStoreId: string;
  errors?: string[];
  onSubmitCompleted: (
    payload?: DeliveryManagementCreateDriverModalDriverCreateMutation['driverCreate']
  ) => void;
  onSubmitError: (error: ApolloError | Error) => void;
  orderNumber: string;
};

const CreateDriverModal = ({
  currentStoreId,
  errors,
  isAdditional = false,
  isOpen = false,
  onDismiss,
  onSubmitCompleted,
  onSubmitError,
  orderNumber,
  stores = [],
}: CreateDriverModalProps) => {
  const [t] = useTranslation();
  const [createDriverMutation, createDriverResult] = useMutation<
    DeliveryManagementCreateDriverModalDriverCreateMutation,
    DeliveryManagementCreateDriverModalDriverCreateMutationVariables
  >(CreateDriverMutation);

  const initFormValues = {
    ezcaterStoreIds: currentStoreId ? [currentStoreId] : [],
    firstName: '',
    lastName: '',
    makeModel: '',
    phone: '',
  };
  const storesSelectOptions = stores.map(store => {
    return {...store, isCurrent: store.isCurrent || store.uuid === currentStoreId};
  });

  const handleSubmit = async (
    {ezcaterStoreIds, ...values}: FormFields,
    {setSubmitting}: FormikHelpers<FormFields>
  ) => {
    setSubmitting(true);
    const storeIds = ezcaterStoreIds.includes(SELECT_ALL_OPTION_VALUE)
      ? stores?.map(store => store.uuid)
      : ezcaterStoreIds;

    try {
      const result = await createDriverMutation({
        variables: {input: {driver: {...values, ezcaterStoreIds: storeIds}}},
      });

      await onSubmitCompleted(result?.data?.driverCreate);
    } catch (e) {
      if (e instanceof Error) {
        await onSubmitError(e);
      } else {
        throw e;
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik<FormFields>
      enableReinitialize
      initialValues={initFormValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validationSchema={validationSchema}
    >
      <CreateDriverForm
        isAdditional={isAdditional}
        isOpen={isOpen}
        onDismiss={onDismiss}
        orderNumber={orderNumber}
        stores={storesSelectOptions}
      >
        {!!errors?.length && (
          <EzAlert
            use="error"
            headline={
              createDriverResult.data?.driverCreate?.driver
                ? t(
                    'components.deliveryManagement.assignDriver.createDriverModal.assignmentErrorHeadline'
                  )
                : t(
                    'components.deliveryManagement.assignDriver.createDriverModal.creationErrorHeadline'
                  )
            }
            tagline={errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          />
        )}
      </CreateDriverForm>
    </Formik>
  );
};

export const CreateDriverMutation = gql`
  mutation DeliveryManagementCreateDriverModalDriverCreateMutation($input: DriverCreateInput!) {
    driverCreate: dmDriverCreate(input: $input) {
      driver {
        id
        firstName
        lastName
        phone
        makeModel
      }
      errors
    }
  }
`;

export default CreateDriverModal;
